import React from 'react';
import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';

// Hooks
import { useFieldValue } from 'Shared/utils/hooks';

// Constants
import Colors from 'Wpds/colors/ColorConstants';

// Utils
import { required, composeValidators, maxLength } from 'Shared/utils/formValidations';

import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import FormInput from 'Shared/components/FormComponents/FormInput';
import FormDateTimePicker from 'Shared/components/FormComponents/FormDateTimePicker';
import FormRichTextInput from 'Shared/components/FormComponents/FormRichTextInput/FormRichTextInput';
import WpIcon from 'Wpds/components/icon/WpIcon';
import FormIconPicker from './FormIconPicker/FormIconPicker';
import MemberField from '../MemberField/MemberField';

// Styles
import './NameAndDescription.scss';

type Props = {
  isEdit: boolean;
};

export default function NameAndDescription({ isEdit }: Props) {
  const workspaceId = useFieldValue('workspaceId');
  const workspaces = useSelector(state => state.workspaces);
  const workspaceUserIds = workspaces.find(w => w.id === workspaceId)?.users?.map(({ id }) => id) || [];

  const removeUsersNotInWorkspace = users => {
    return users.filter(u => workspaceUserIds.includes(u.id));
  };

  return (
    <>
      <div className="d-flex w-100 goals-name-and-description-group">
        <FieldGroup
          label="Goal name"
          labelClassName="goals-name-and-description-field-label"
          className={'w-75'}
          // className={!showChooseMemberField ? 'w-100 mr-0' : ''}
          required
          labelRequired="*required"
        >
          <Field
            name="name"
            autoFocus
            validate={composeValidators(required, maxLength(100))}
            component={FormInput}
            placeholder="Ex: Improve a specific metric or soft skill"
          />
        </FieldGroup>
        <FieldGroup
          label="Who is the owner of this goal?"
          labelClassName="goals-name-and-description-field-label"
          required
          labelRequired="*required"
        >
          {isEdit ? (
            <MemberField
              name="assigneeId"
              disabled={workspaceUserIds.length < 2}
              membersFilter={removeUsersNotInWorkspace}
            />
          ) : (
            <MemberField name="assigneeId" />
          )}
        </FieldGroup>
      </div>

      <div className="d-flex w-100 goals-name-and-description-group mt-3 pb-3">
        <FieldGroup
          label="Goal End Date"
          labelClassName="goals-name-and-description-field-label"
          required
          labelRequired="*required"
        >
          <Field
            name="endDate"
            component={FormDateTimePicker}
            className="goals-name-and-description-date-picker"
            validate={required}
            placeholder="Choose date"
            dateFormat="ll"
            timeFormat={false}
            icon={<WpIcon name="calendar" color={Colors.gray[500]} />}
          />
        </FieldGroup>
        <FieldGroup label="Choose Icon" labelClassName="goals-name-and-description-field-label">
          <Field name="icon" component={FormIconPicker} />
        </FieldGroup>
      </div>
      <FieldGroup label="Goal Description" labelClassName="goals-name-and-description-field-label">
        <Field
          name="description"
          component={FormRichTextInput}
          className="goals-name-and-description-description"
          placeholder="Add more detail here to help remember why you created this goal"
          qaTest="goal-description-rich-text-input"
        />
      </FieldGroup>
    </>
  );
}
